import get from 'lodash/get';
import reduce from 'lodash/reduce';
import isUndefined from 'lodash/isUndefined';
import isEqual from 'lodash/isEqual';
import findIndex from 'lodash/findIndex';
import isNil from 'lodash/isNil';

import { SAVED_CART_KEY, SAVED_STORECREDIT_KEY } from 'app/constants';

const GIFT_CARD_PREFIX = 'v-gift-card';

export const createCartAddress = (
    address1,
    address2,
    city,
    state,
    postalCode,
    country,
    firstName,
    lastName,
    phoneNumber
) => ({
    address1,
    address2,
    city,
    state,
    postalCode,
    country,
    firstName,
    lastName,
    phoneNumber
});

export const getProductSalePrice = (sale) => {
    // Because the type of `null` is `object` :unamused:
    if (typeof sale === 'object' && sale !== null) {
        return sale.value;
    }
    return sale;
};

export const productIsOnSale = (product) => {
    if (product.pricing && product.pricing.salePrice) {
        return !!getProductSalePrice(product.pricing.salePrice);
    }
    return false;
};

export const getProductListPrice = (product) => {
    return product.pricing ? product.pricing.listPrice : product.price;
};

export const getProductPrice = (product) => {
    return productIsOnSale(product) ?
        getProductSalePrice(product.pricing.salePrice) :
        getProductListPrice(product);
};

export const getProductSubtotal = (product, quantity) => {
    const price = parseFloat(getProductPrice(product), 10);
    return price * parseFloat(quantity, 10);
};

export const getNumberOfCartItems = (cartItems) => {
    return reduce(cartItems, (result, item) => {
        return result + item.quantity;
    }, 0);
};

export const isShippingAndBillingAddressSame = ({ shippingAddress, billingAddress }) => {
    return !isUndefined(shippingAddress)
        && !isUndefined(billingAddress)
        && isEqual(shippingAddress, billingAddress);
};

export const getShippingMethodIndex = (availableShippingMethods, shippingMethod) => {
    const notFoundIndex = -1;
    if (isUndefined(availableShippingMethods)) return notFoundIndex;
    if (isUndefined(shippingMethod)) return undefined;

    let index;
    if (shippingMethod.shippingBand && !isUndefined(shippingMethod.shippingBand.shippingPrice)) {
        index = findIndex(availableShippingMethods, {
            name: shippingMethod.name,
            shippingPrice: shippingMethod.shippingBand.shippingPrice
        });
        if (index === notFoundIndex) {
            index = findIndex(availableShippingMethods, {
                name: shippingMethod.name,
                shippingCost: shippingMethod.shippingBand.shippingPrice
            });
        }
    } else {
        index = findIndex(availableShippingMethods, {
            name: shippingMethod.name,
            shippingCost: shippingMethod.shippingCost
        });
        if (index === notFoundIndex) {
            index = findIndex(availableShippingMethods, {
                name: shippingMethod.name,
                shippingPrice: shippingMethod.shippingPrice
            });
        }
    }
    return (index !== notFoundIndex) ? index : undefined;
};

export const hasValidShippingMethod = (availableShippingMethods, shippingMethod) => {
    const index = getShippingMethodIndex(availableShippingMethods, shippingMethod);
    return angular.isDefined(index) && index >= 0;
};

export const getItemsTotal = (cartItems = []) => {
    return cartItems.reduce((total, item) => {
        if (item.product) {
            return total + (item.quantity * getOfferPrice(item.product.pricing, item.product.price));
        }

        return total + ((item.quantity * item.price.amount) / 100);
    }, 0);
};

export const getOfferPrice = (pricing, price) => {
    if (isNil(pricing)) {
        return price;
    }
    return !isNil(pricing.salePrice)
        ? pricing.salePrice
        : pricing.listPrice;
};

export const isShippingRequiredOnCart = (cartItems = []) => {
    return cartItems.some(
        cartItem => (cartItem.isShippable || cartItem.isShippable === null) ||
            (cartItem.product && (cartItem.product.requiresShipping || cartItem.product.requiresShipping === null))
    );
};

export const isGiftCardInLegacyCart = (cartItems = []) => {
    return cartItems.findIndex(cartItem => cartItem.product.sku.toLowerCase().startsWith(GIFT_CARD_PREFIX)) > -1;
};

export const getSavedCartInfo = () => {
    const stringifiedCart = window.sessionStorage.getItem(SAVED_CART_KEY) || '{}';
    return JSON.parse(stringifiedCart);
};

export const setSavedCartInfo = (cart) => {
    const stringifiedCart = JSON.stringify(cart);
    window.sessionStorage.setItem(SAVED_CART_KEY, stringifiedCart);
};

export const getSavedStoreCreditInfo = () => {
    const stringifiedStoreCreditInfo = window.sessionStorage.getItem(SAVED_STORECREDIT_KEY);

    if (!stringifiedStoreCreditInfo) {
        return { items: [], storeCreditAmountInCents: 0 };
    }

    return JSON.parse(stringifiedStoreCreditInfo);
};

export const setSavedStoreCreditInfo = (storeCreditInfo) => {
    const stringifiedStoreCreditInfo = JSON.stringify(storeCreditInfo);
    window.sessionStorage.setItem(SAVED_STORECREDIT_KEY, stringifiedStoreCreditInfo);
};

export const clearSavedStoreCreditInfo = () => {
    const defaultStoreCreditInfo = { items: [], storeCreditAmountInCents: 0 };
    setSavedStoreCreditInfo(defaultStoreCreditInfo);
};

// new cart utils

export const isGiftCardInCart = (cartItems = []) => {
    return cartItems.findIndex(cartItem => cartItem.variant.sku.toLowerCase().startsWith(GIFT_CARD_PREFIX)) > -1;
};

export const getCartTotalAmountInCents = (cart) => {
    return get(cart, 'pricing.total.amount', 0);
};

export const dollarsToCents = dollars => Math.round(dollars * 100);

export function formatZipCode(zip, country) {
    if (country === 'US') {
        if (zip.length === 9) {
            return `${zip.slice(0, 5)}-${zip.slice(5)}`;
        }
    }
    return zip;
}



// WEBPACK FOOTER //
// ./src/app/services/cartUtils.js